import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useI18n } from "../../i18n";
import { cn } from "../../lib/utils";
import { Logo } from "../ui/Logo";
import { Button } from "../ui/Button";
import { LanguageSelector } from "../ui/LanguageSelector";
import { ThemeToggle } from "../ui/ThemeToggle";
import { useDemo } from "../demo/DemoContext";

export function Header() {
  const { t } = useI18n();
  const { openDemo } = useDemo();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/onboarding", label: t("nav.onboarding") },
    { to: "/autenticacao", label: t("nav.auth") },
    { to: "/recursos", label: t("nav.features") },
    { to: "/seguranca", label: t("nav.security") },
    { to: "/contato", label: t("nav.contact") },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="hidden border-b border-border/60 bg-background/80 text-xs text-muted-foreground md:block">
        <div className="container-x flex h-9 items-center justify-between">
          <span className="inline-flex items-center gap-2 font-mono">
            <ShieldCheck className="h-3.5 w-3.5 text-brand" />
            {t("topbar.tagline")}
          </span>
          <span className="font-mono uppercase tracking-wider">{t("topbar.status")}</span>
        </div>
      </div>

      {/* Header */}
      <header
        className={cn(
          "sticky top-0 z-50 border-b transition-colors",
          scrolled
            ? "border-border bg-background/85 backdrop-blur-xl"
            : "border-transparent bg-background/40 backdrop-blur"
        )}
      >
        <div className="container-x flex h-16 items-center justify-between gap-4">
          <Link to="/" aria-label="SecFace">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-brand/12 text-brand"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden md:block">
              <ThemeToggle />
            </div>
            <div className="hidden md:block">
              <LanguageSelector />
            </div>
            <Button size="sm" className="hidden sm:inline-flex" onClick={openDemo}>
              {t("demo.cta")}
            </Button>
            <button
              onClick={() => setOpen((o) => !o)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border lg:hidden"
              aria-label={t("nav.menu")}
              aria-expanded={open}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="border-t border-border bg-background lg:hidden">
            <div className="container-x flex flex-col gap-1 py-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    cn(
                      "rounded-lg px-3 py-2.5 text-sm font-medium",
                      isActive ? "bg-brand/12 text-brand" : "text-foreground hover:bg-accent"
                    )
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-3 flex flex-col gap-2">
                <Button size="sm" className="w-full" onClick={() => { setOpen(false); openDemo(); }}>
                  {t("demo.cta")}
                </Button>
                <Button to="/contato" size="sm" variant="outline" className="w-full">
                  {t("nav.bookDemo")}
                </Button>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <LanguageSelector align="left" />
                <ThemeToggle />
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
